import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchAirports } from '../../utils/airportSearchNew';

const GroupedAirportSearch = ({
  inputValue,
  onSelect,
  placeholder = "Search airports...",
  className = "",
  disabled = false,
  showResults = true,
  limit = 15,
  minSearchLength = 2,
  debounceMs = 300,
  onInputChange,
  autoFocus = false,
  readOnly = false,
  hasError = false,
  onDropdownToggle,
  groupResults = true
}) => {
  const [searchTerm, setSearchTerm] = useState(inputValue || '');
  const [results, setResults] = useState({ groups: [], total: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  // Debounced search function
  const debouncedSearch = useCallback(async (term) => {
    if (term.length < minSearchLength) {
      setResults({ groups: [], total: 0 });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const searchResults = await searchAirports(term, { 
        limit,
        groupResults: groupResults
      });
      
      if (groupResults && searchResults.groups) {
        setResults(searchResults);
      } else {
        // Convert single results to grouped format for consistency
        setResults({
          groups: [{
            type: 'all',
            title: 'Airports',
            results: searchResults.slice(0, limit)
          }],
          total: searchResults.length
        });
      }
    } catch (error) {
      console.error('Airport search error:', error);
      setResults({ groups: [], total: 0 });
    } finally {
      setIsLoading(false);
    }
  }, [limit, minSearchLength, groupResults]);

  // Handle input changes with debouncing
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (searchTerm.trim() && showResults) {
        debouncedSearch(searchTerm.trim());
      } else {
        setResults({ groups: [], total: 0 });
        setIsLoading(false);
      }
    }, debounceMs);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm, debouncedSearch, debounceMs, showResults]);

  // Update search term when inputValue prop changes
  useEffect(() => {
    setSearchTerm(inputValue || '');
  }, [inputValue]);

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedIndex(-1);
    setSelectedGroupIndex(-1);
    
    if (value.trim()) {
      setIsOpen(true);
      onDropdownToggle?.(true);
    } else {
      setIsOpen(false);
      onDropdownToggle?.(false);
    }
    
    onInputChange?.(value);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (searchTerm.trim() && results.total > 0) {
      setIsOpen(true);
      onDropdownToggle?.(true);
    }
  };

  // Handle input blur
  const handleInputBlur = (e) => {
    // Delay closing to allow for clicks on dropdown items
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
        onDropdownToggle?.(false);
      }
    }, 150);
  };

  // Handle airport selection
  const handleSelect = (airport) => {
    setSearchTerm(airport.city_name);
    setIsOpen(false);
    onDropdownToggle?.(false);
    onSelect?.(airport);
  };

  // Get all airports in flat array for keyboard navigation
  const getAllAirports = () => {
    const allAirports = [];
    results.groups.forEach((group, groupIndex) => {
      group.results.forEach((airport, airportIndex) => {
        allAirports.push({ airport, groupIndex, airportIndex });
      });
    });
    return allAirports;
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || results.total === 0) return;

    const allAirports = getAllAirports();
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < allAirports.length - 1 ? prev + 1 : 0
        );
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : allAirports.length - 1
        );
        break;
        
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < allAirports.length) {
          handleSelect(allAirports[selectedIndex].airport);
        }
        break;
        
      case 'Escape':
        setIsOpen(false);
        onDropdownToggle?.(false);
        inputRef.current?.blur();
        break;
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
        onDropdownToggle?.(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onDropdownToggle]);

  const inputClassName = `
    form-control 
    ${className} 
    ${hasError ? 'is-invalid border-danger' : ''}
    ${disabled ? 'disabled' : ''}
  `.trim();

  return (
    <div className="position-relative" style={{ width: '100%', zIndex: 1050 }}>
      <input
        ref={inputRef}
        type="text"
        className={inputClassName}
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        readOnly={readOnly}
        autoFocus={autoFocus}
        autoComplete="off"
        style={{
          borderColor: hasError ? '#dc3545' : undefined,
          boxShadow: hasError ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : undefined
        }}
      />
      
      {isOpen && showResults && !readOnly && (
        <div
          ref={dropdownRef}
          className="position-absolute w-100 bg-white border rounded shadow-lg"
          style={{
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: '400px',
            overflowY: 'auto',
            zIndex: 1060,
            marginTop: '2px'
          }}
        >
          {isLoading ? (
            <div className="p-3 text-center text-muted">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Searching...
            </div>
          ) : results.total === 0 ? (
            <div className="p-3 text-center text-muted">
              {searchTerm.length < minSearchLength 
                ? `Type at least ${minSearchLength} characters to search`
                : 'No airports found'
              }
            </div>
          ) : (
            <div>
              {results.groups.map((group, groupIndex) => (
                <div key={`group-${groupIndex}`}>
                  {/* Group Header - Professional styling */}
                  <div 
                    className="px-3 py-2 bg-light border-bottom"
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: '700',
                      color: '#003663', // Darker blue for better contrast
                      backgroundColor: '#f1f3f5', // Slightly darker background
                      letterSpacing: '0.3px',
                      textTransform: 'uppercase',
                      borderBottom: '2px solid #dee2e6'
                    }}
                  >
                    {group.title}
                    <span className="ms-2" style={{ 
                      color: '#6c757d',
                      fontWeight: '500',
                      fontSize: '0.8rem' 
                    }}>
                      ({group.results.length})
                    </span>
                  </div>
                  
                  {/* Group Results with Country Flags - Indented for cleaner look */}
                  {group.results.map((airport, airportIndex) => {
                    const flatIndex = getAllAirports().findIndex(
                      item => item.groupIndex === groupIndex && item.airportIndex === airportIndex
                    );
                    const isSelected = selectedIndex === flatIndex;
                    
                    return (
                      <div
                        key={`${groupIndex}-${airportIndex}-${airport.id}`}
                        className={`cursor-pointer border-bottom ${isSelected ? 'bg-primary text-white' : 'hover:bg-light'}`}
                        style={{
                          backgroundColor: isSelected ? '#0d6efd' : undefined,
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease',
                          paddingLeft: '2rem', // Indented to the right for cleaner hierarchy
                          paddingRight: '0.75rem',
                          paddingTop: '0.75rem',
                          paddingBottom: '0.75rem',
                          marginLeft: '0.5rem', // Additional left margin for visual separation
                          borderLeft: '3px solid transparent' // Subtle visual indicator
                        }}
                        onClick={() => handleSelect(airport)}
                        onMouseEnter={(e) => {
                          setSelectedIndex(flatIndex);
                          // Add subtle border on hover for better visual feedback
                          if (!isSelected) {
                            e.currentTarget.style.borderLeft = '3px solid #e9ecef';
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }
                        }}
                        onMouseLeave={(e) => {
                          // Reset border and background when not selected
                          if (!isSelected) {
                            e.currentTarget.style.borderLeft = '3px solid transparent';
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <div className="d-flex align-items-start">
                          {/* Country Flag */}
                          <span 
                            className="me-2 flex-shrink-0"
                            style={{ 
                              fontSize: '18px',
                              lineHeight: '1.2'
                            }}
                          >
                            {airport.countryFlag || '🏳️'}
                          </span>
                          
                          <div className="flex-grow-1 min-width-0">
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="flex-grow-1 min-width-0">
                                <div 
                                  className={`fw-medium ${isSelected ? 'text-white' : 'text-dark'}`}
                                  style={{ 
                                    fontSize: '0.95rem',
                                    lineHeight: '1.3'
                                  }}
                                >
                                  {airport.city_name}
                                  {group.type === 'exact_code' && (
                                    <span 
                                      className={`ms-2 px-2 py-1 rounded text-xs ${isSelected ? 'bg-white text-primary' : 'bg-primary text-white'}`}
                                      style={{ 
                                        fontSize: '0.75rem',
                                        fontWeight: '600'
                                      }}
                                    >
                                      Exact
                                    </span>
                                  )}
                                </div>
                                <div 
                                  className={`small ${isSelected ? 'text-white-50' : 'text-muted'}`} 
                                  style={{ 
                                    fontSize: '0.8rem',
                                    lineHeight: '1.2',
                                    marginTop: '2px'
                                  }}
                                >
                                  {airport.name}
                                </div>
                              </div>
                              <div 
                                className={`ms-2 small fw-medium ${isSelected ? 'text-white' : 'text-primary'}`}
                                style={{ 
                                  fontSize: '0.85rem',
                                  fontWeight: '600'
                                }}
                              >
                                {airport.id}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GroupedAirportSearch; 